import getDatapointStatus from 'owa-analytics/lib/utils/getDatapointStatus';
import { ActionMetaInfo, AppContext, AppError, CustomData } from 'mini-app-context';
import { executeDiagnosticsTask } from 'mini-diagnostics/lib/utils/executeDiagnosticsTask';
import { PerformanceDatapoint } from 'owa-analytics';
import { trace } from 'owa-trace';
import { trackWorkflowActivity } from 'mini-diagnostic-store/lib/utils/trackWorkflowActivity';
import { WORKFLOW_STATES } from 'mini-diagnostics/lib/schema/WorkflowStates';

import {
    getWorkflowsForCheckmarkSignal,
    getWorkflowName,
    isCheckmarkSignal,
    isEndSignal,
    isStartSignal,
    isWorkflowWithMultipleBranches,
    areWorkflowsPresent,
    getOptions,
    getWorkflowsForEndSignal,
    isWorkflowInternal,
} from 'mini-app-context/lib/utils/workflowUtils';

const perfDpBuffer = {};

/**
 * Listener to log the performance and reliability datapoints
 */
export function perfAndReliabilityListener(
    metaInfo: ActionMetaInfo,
    ctx: AppContext,
    error?: AppError
): void {
    if (!areWorkflowsPresent()) {
        trace.warn(
            `perfAndReliabilityListener: Workflows not present for ${metaInfo?.name}-${metaInfo?.actionSource}`
        );
        return;
    }
    const { name, actionSource, customData } = metaInfo;
    delete customData?.isUIAction;
    if (isStartSignal(name)) {
        handleStartSignal(name, actionSource, customData, ctx);
    }
    if (isEndSignal(name)) {
        handleEndSignal(name, actionSource, customData, ctx, error);
    }
    if (isCheckmarkSignal(name)) {
        handleCheckmarkSignal(name, actionSource, customData, ctx);
    }
}

/**
 * Internal function to handle start signal
 */
function handleStartSignal(
    name: string,
    actionSource: string,
    customData: CustomData,
    ctx: AppContext
) {
    if (perfDpBuffer[name]) {
        // If the perf dp for the start signal is already started, invalidate the same and create a new one
        let perfdp: PerformanceDatapoint;
        if ((perfdp = perfDpBuffer[name][ctx.correlationId])) {
            perfdp.invalidate();
        }
        perfDpBuffer[name][ctx.correlationId] = createPerfDp(name);
    } else {
        perfDpBuffer[name] = {
            [ctx.correlationId]: createPerfDp(name),
        };
    }
    let cdata = customData;
    if (!isWorkflowInternal(name)) {
        cdata = { ...cdata, isUIAction: true };
    }
    perfDpBuffer[name][ctx.correlationId].addActionSource(actionSource);
    perfDpBuffer[name][ctx.correlationId].addCustomData(cdata);

    // trigger trackWorkflow diagnostics event
    executeDiagnosticsTask(() =>
        trackWorkflowActivity(getWorkflowName(name), WORKFLOW_STATES.WorkflowStart, {
            ...customData,
        })
    );
}

/**
 * Internal function to handle end signal
 */
function handleEndSignal(
    name: string,
    actionSource: string,
    customData: CustomData,
    ctx: AppContext,
    appError?: AppError
) {
    const workflows = getWorkflowsForEndSignal(name);
    delete customData.cId; // avoid logging cId again
    workflows.forEach(workflow => {
        let perfdp: PerformanceDatapoint;
        if ((perfdp = perfDpBuffer[workflow.start]?.[ctx.correlationId])) {
            const cdata = !isWorkflowWithMultipleBranches(workflow)
                ? customData
                : { ...customData, startSignal: workflow.start, endSignal: name };
            // need not add ctx id again because it is already added as part of custom data when creating the perf dp on start signal arrival
            perfdp.addCustomData(addSourceToCustomData(actionSource, cdata));
            if (appError) {
                const { error, status } = appError;
                perfdp.endWithError(
                    status || getDatapointStatus(error?.message, error).status,
                    error
                );
            } else {
                perfdp.end();
            }
        }

        // trigger trackWorkflow diagnostics event
        executeDiagnosticsTask(() =>
            trackWorkflowActivity(workflow.name, WORKFLOW_STATES.WorkflowEnd, {
                ...customData,
                error: appError?.error,
            })
        );
    });
}

/**
 * Internal function to handle check mark signals
 */
function handleCheckmarkSignal(
    name: string,
    actionSource: string,
    customData: CustomData,
    ctx: AppContext
) {
    const workflows = getWorkflowsForCheckmarkSignal(name);
    delete customData.cId; // avoid logging cId again
    workflows.forEach(workflow => {
        let perfdp;
        if ((perfdp = perfDpBuffer[workflow.start]?.[ctx.correlationId])) {
            perfdp.addCheckmark(name);
            perfdp.addCustomData(addSourceToCustomData(actionSource, customData));
        }

        // trigger trackWorkflow diagnostics event
        executeDiagnosticsTask(() =>
            trackWorkflowActivity(workflow.name, WORKFLOW_STATES.WorkflowCheckpoint, customData)
        );
    });
}

/**
 * Internal function to add action source if any to custom data and return the updated custom data
 */
function addSourceToCustomData(actionSource: string, customData: CustomData) {
    let cdata = customData;
    if (actionSource) {
        const sources =
            cdata && 'sources' in cdata
                ? JSON.parse(cdata.sources as string).push(actionSource)
                : [actionSource];
        cdata = { ...cdata, sources: JSON.stringify(sources) };
    }
    return cdata;
}

/**
 * Internal function to create a performance datapoint
 */
function createPerfDp(startSignal: string) {
    const perfDpName = getWorkflowName(startSignal);
    return new PerformanceDatapoint(perfDpName, getOptions(startSignal));
}
